import { z } from 'zod'

const optionalText = (max: number) =>
  z.preprocess(
    (value) => (typeof value === 'string' && value.trim() === '' ? undefined : value),
    z.string().trim().max(max).optional(),
  )

const clearableOptionalText = (max: number) =>
  z.preprocess(
    (value) => (typeof value === 'string' && value.trim() === '' ? null : value),
    z.string().trim().max(max).nullable().optional(),
  )

const optionalMoney = z.number().int().min(1).max(100_000_000).optional()

const requiredMoney = z.number().int().min(1).max(100_000_000)

export const upsertBudgetPlanSchema = z.object({
  totalAmount: requiredMoney,
})

export const createBudgetCategorySchema = z.object({
  name: z.string().trim().min(1).max(120),
})

export const updateBudgetCategorySchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
})

export const createBudgetExpenseSchema = z.object({
  title: z.string().trim().min(1).max(160),
  amount: requiredMoney,
})

export const updateBudgetExpenseSchema = z.object({
  categoryId: z.uuid().optional(),
  title: z.string().trim().min(1).max(160).optional(),
  amount: optionalMoney,
})

const optionalDate = z.preprocess(
  (value) => (typeof value === 'string' && value.trim() === '' ? undefined : value),
  z.iso.date().optional(),
)

const clearableOptionalDate = z.preprocess(
  (value) => (typeof value === 'string' && value.trim() === '' ? null : value),
  z.iso.date().nullable().optional(),
)

export const createBudgetPaymentSchema = z.object({
  name: z.string().trim().min(1).max(160),
  amount: requiredMoney,
  dueDate: optionalDate,
  paidAt: optionalDate,
  paymentMethod: z.enum(['cash', 'card', 'transfer']).optional(),
  description: optionalText(2000),
  sortOrder: z.number().int().min(0).max(10_000).default(0),
})

export const updateBudgetPaymentSchema = z.object({
  name: z.string().trim().min(1).max(160).optional(),
  amount: optionalMoney,
  dueDate: clearableOptionalDate,
  paidAt: clearableOptionalDate,
  paymentMethod: z.enum(['cash', 'card', 'transfer']).nullable().optional(),
  description: clearableOptionalText(2000),
  sortOrder: z.number().int().min(0).max(10_000).optional(),
})

export type CreateBudgetPaymentInput = z.infer<typeof createBudgetPaymentSchema>
export type UpdateBudgetPaymentInput = z.infer<typeof updateBudgetPaymentSchema>
export type UpsertBudgetPlanInput = z.infer<typeof upsertBudgetPlanSchema>
export type CreateBudgetCategoryInput = z.infer<typeof createBudgetCategorySchema>
export type UpdateBudgetCategoryInput = z.infer<typeof updateBudgetCategorySchema>
export type CreateBudgetExpenseInput = z.infer<typeof createBudgetExpenseSchema>
export type UpdateBudgetExpenseInput = z.infer<typeof updateBudgetExpenseSchema>
